/* ─────────────────────────────────────────────────────────────────────────────
 * CPF e CNPJ: mascara e verificacao dos digitos.
 *
 * A verificacao existe porque o formulario de credenciamento sai daqui por
 * e-mail e nao passa por nenhuma validacao de servidor. Um digito trocado so
 * apareceria no momento do repasse — semanas depois, com o credenciado ja
 * atendendo. Barrar na digitacao custa uma linha e evita esse retrabalho.
 *
 * O que isto NAO faz: confirmar que o documento existe na Receita. Digito
 * verificador so prova que a sequencia e bem formada.
 * ────────────────────────────────────────────────────────────────────────── */

const CPF_DIGITS = 11
const CNPJ_DIGITS = 14

export function onlyDigits(value: string): string {
  return value.replace(/\D/g, "")
}

/** 000.000.000-00 */
export function formatCpf(value: string): string {
  const d = onlyDigits(value).slice(0, CPF_DIGITS)

  return d
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d{1,2})$/, ".$1-$2")
}

/** 00.000.000/0000-00 */
export function formatCnpj(value: string): string {
  const d = onlyDigits(value).slice(0, CNPJ_DIGITS)

  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2")
}

/**
 * Mascara unica para o campo "CNPJ / CPF" da ficha.
 *
 * A ficha tem um campo so para os dois, e a pessoa juridica e a fisica
 * preenchem o mesmo lugar. Ate onze digitos a mascara e de CPF; a partir do
 * decimo segundo ela vira CNPJ sozinha, sem exigir que alguem escolha antes.
 */
export function formatTaxId(value: string): string {
  const d = onlyDigits(value)
  return d.length <= CPF_DIGITS ? formatCpf(d) : formatCnpj(d)
}

/** Digito verificador por soma ponderada, comum a CPF e CNPJ. */
function checkDigit(digits: number[], weights: number[]): number {
  const sum = digits.reduce((total, digit, index) => total + digit * weights[index], 0)
  const rest = sum % 11
  return rest < 2 ? 0 : 11 - rest
}

export function isValidCpf(value: string): boolean {
  const d = onlyDigits(value)
  if (d.length !== CPF_DIGITS) return false

  // 111.111.111-11 e companhia passam na conta dos digitos verificadores.
  if (/^(\d)\1+$/.test(d)) return false

  const numbers = d.split("").map(Number)
  const first = checkDigit(numbers.slice(0, 9), [10, 9, 8, 7, 6, 5, 4, 3, 2])
  const second = checkDigit(numbers.slice(0, 10), [11, 10, 9, 8, 7, 6, 5, 4, 3, 2])

  return first === numbers[9] && second === numbers[10]
}

export function isValidCnpj(value: string): boolean {
  const d = onlyDigits(value)
  if (d.length !== CNPJ_DIGITS) return false
  if (/^(\d)\1+$/.test(d)) return false

  const numbers = d.split("").map(Number)
  const first = checkDigit(numbers.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2])
  const second = checkDigit(numbers.slice(0, 13), [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2])

  return first === numbers[12] && second === numbers[13]
}

/** Aceita CPF ou CNPJ — o campo da ficha e um so. */
export function isValidTaxId(value: string): boolean {
  const d = onlyDigits(value)
  if (d.length === CPF_DIGITS) return isValidCpf(d)
  if (d.length === CNPJ_DIGITS) return isValidCnpj(d)
  return false
}
